import { Capacitor } from "@capacitor/core";

export const getAuthToken = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem('token');
};

const DEFAULT_ANDROID_API_BASE_URL = "https://mmjdctv4-9065.brs.devtunnels.ms";

const normalizeBaseUrl = (value: string | undefined) => {
  if (!value) return '';
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const resolveCraBaseUrl = () =>
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL;

const resolveViteBaseUrl = () =>
  typeof import.meta !== "undefined" &&
  (import.meta as any).env
    ? (import.meta as any).env.VITE_API_BASE_URL ||
      (import.meta as any).env.VITE_API_URL
    : undefined;

const resolveEnvBaseUrl = () => {
  const craBase = resolveCraBaseUrl();
  if (craBase) return craBase;

  const viteBase = resolveViteBaseUrl();
  if (viteBase) return viteBase;

  // Default to same-origin so dev servers can proxy /api
  return "";
};

const resolveNativeBaseUrl = () => {
  const craNativeBase =
    process.env.REACT_APP_ANDROID_API_BASE_URL ||
    process.env.REACT_APP_ANDROID_API_URL ||
    resolveCraBaseUrl();

  const viteNativeBase =
    typeof import.meta !== "undefined" &&
    (import.meta as any).env
      ? (import.meta as any).env.VITE_ANDROID_API_BASE_URL ||
        (import.meta as any).env.VITE_ANDROID_API_URL ||
        resolveViteBaseUrl()
      : undefined;

  return craNativeBase || viteNativeBase || DEFAULT_ANDROID_API_BASE_URL;
};

const isNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export const API_BASE_URL = normalizeBaseUrl(
  isNativePlatform() ? resolveNativeBaseUrl() : resolveEnvBaseUrl()
);

const resolveUrl = (rawPath: string) => {
  const path = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;

  if (!API_BASE_URL) {
    return path;
  }

  // Avoid double-prefixing when the path already contains the base
  if (path.startsWith(API_BASE_URL)) {
    return path;
  }

  return `${API_BASE_URL}${path}`;
};

export const apiFetch = (path: string, init?: RequestInit) => {
  const headers = new Headers(init?.headers ?? {});
  const token = getAuthToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const body = init?.body;
  const isJsonBody =
    body !== undefined &&
    !(body instanceof FormData) &&
    typeof body !== 'string' &&
    !(body instanceof Blob);

  if (isJsonBody || typeof body === 'string') {
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
  }

  return fetch(resolveUrl(path), {
    ...init,
    headers,
  });
};

export class ApiError extends Error {
  status?: number;
}

export const requestJson = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const response = await apiFetch(path, init);
  if (!response.ok) {
    let message = 'No se pudo completar la solicitud.';
    try {
      const text = await response.text();
      if (text) {
        try {
          const data = JSON.parse(text);
          if (data && typeof data.message === 'string' && data.message.trim().length > 0) {
            message = data.message;
          } else {
            message = text;
          }
        } catch {
          message = text;
        }
      }
    } catch {
      // ignore parsing errors
    }
    const isAuthError = response.status === 401 || response.status === 403;
    const error = new ApiError(
      isAuthError
        ? message || 'No tienes permisos para realizar esta acción.'
        : message
    );
    error.status = response.status;
    throw error;
  }

  if (response.status === 204 || response.status === 304) {
    return undefined as T;
  }

  return (await response.json()) as T;
};

export const resolveApiUrl = resolveUrl;
